import { Box, Flex, Link, PartnerLogo, Typography } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { PropsWithChildren } from 'react';
import { useState } from 'react';

import { useKioskSpacing } from '@jane/kiosk/hooks';

import { useSpecialDetailContext } from './context';

import styles from './SpecialDetail.module.css';

export type SpecialDetailStatusHeaderProps = PropsWithChildren<{
  brandImage?: string;
  brandName?: string;
  specialDescription: string;
  specialTerms?: string;
  specialTitle: string;
}>;

export const SpecialDetailHeader = ({
  brandImage,
  brandName,
  children,
  specialDescription,
  specialTitle,
  specialTerms,
}: SpecialDetailStatusHeaderProps) => {
  const [showTerms, setShowTerms] = useState(false);
  const { isIpad } = useKioskSpacing();
  const { scrolledFromTop } = useSpecialDetailContext();

  const isGold = !!brandName;

  return (
    <Flex
      flexDirection="column"
      className={clsx(styles['specialDetail__transition'], {
        [styles['box__shadow__soft']]: !scrolledFromTop,
      })}
      borderRadius={scrolledFromTop ? undefined : 'lg'}
      p={scrolledFromTop ? 0 : isIpad ? 24 : 40}
    >
      <Box>
        <Flex
          alignItems="center"
          gap={scrolledFromTop ? 24 : 40}
          className={clsx(styles['specialDetail__transition'])}
        >
          {isGold && (
            <PartnerLogo
              name={brandName}
              image={brandImage}
              variant="brand"
              size={scrolledFromTop ? 'sm' : 'xl'}
              data-testid="partner-logo"
              className={clsx(styles['specialDetail__transition'])}
            />
          )}

          <Typography
            branded
            variant={scrolledFromTop ? 'h3-semibold' : 'h2-semibold'}
            className={clsx(styles['specialDetail__transition'])}
          >
            {specialTitle}
          </Typography>
        </Flex>

        {(specialDescription || specialTerms) &&
          (!scrolledFromTop || isIpad) && (
            <Flex flexDirection="column" pt={24}>
              {specialDescription && (
                <Typography>{specialDescription}</Typography>
              )}

              {specialTerms && showTerms && (
                <Typography pt={8}>{specialTerms}</Typography>
              )}

              {specialTerms && (
                <Link
                  color="grays-mid"
                  onClick={() => setShowTerms((prev) => !prev)}
                >
                  <Typography variant="body-semibold" pt={8}>
                    {showTerms ? 'Hide' : 'View'} terms & conditions
                  </Typography>
                </Link>
              )}
            </Flex>
          )}
      </Box>

      {children}
    </Flex>
  );
};
